import dynamic from "next/dynamic";
import React from "react";
import { StoryblokBlockComponent } from "../../lib/storyblok/types/StoryblokComponent";
import { Tabs, Tab } from "../../ui/Common/Tabs";
import Button from "../../ui/Button";
import { useLinks } from "../../lib/storyblok/useLinks";
import { TabsGridBlock } from "../../lib/storyblok/types/blocks/TabsGridBlock";
import VideoPlayer from "../../ui/Common/VideoPlayer";
import TabsGridUi from "../../ui/TabsGrid";
import StoryblokImage from "../StoryblokImage";
import Image from "next/image";
import { isValidImageData } from "../../lib/utils/imageUtils";

const AnimatePresence = dynamic(
  () => import("framer-motion").then((mod) => mod.AnimatePresence),
  {
    ssr: false,
  }
);

const TabsGrid: StoryblokBlockComponent<TabsGridBlock> = ({
  title,
  description,
  buttons,
  outerSpacing,
  width,
  tabs,
}) => {
  const resolveLink = useLinks();
  const [activeTab, setActiveTab] = React.useState(0);

  const tabIntroButtonCollection = React.useMemo(
    () =>
      buttons?.map((button) => (
        <Button
          variant={button.buttonType || "default"}
          key={button._uid}
          href={button.buttonLink ? resolveLink(button.buttonLink) : ""}
        >
          {button.buttonText}
        </Button>
      )),
    [buttons, resolveLink]
  );

  const createItemButtonCollection = React.useCallback(
    (itemButtons, itemCover) => {
      if (itemButtons && itemButtons.length > 0) {
        const button = itemButtons[0];
        return (
          <Button
            variant={button.buttonType ?? "default"}
            size={itemCover ? "medium" : "small"}
          >
            {button.buttonText}
          </Button>
        );
      }
      return null;
    },
    []
  );

  const handleTabClick = (index: number) => {
    setActiveTab(index);
  };

  return (
    <TabsGridUi
      title={title}
      description={description}
      buttons={tabIntroButtonCollection}
      outerSpacing={outerSpacing}
      width={width}
    >
      <Tabs activeIndex={activeTab} alignment={"alignCenter"}>
        {tabs.map((tab, i) => (
          <Tab
            key={tab._uid}
            index={i}
            title={tab.tabTitle}
            isActive={activeTab === i}
            onClick={() => handleTabClick(i)}
          />
        ))}
      </Tabs>
      <TabsGridUi.TabHeader
        description={tabs[activeTab].gridDescription}
        index={activeTab}
      />
      <AnimatePresence>
        {tabs.map(
          (tab, j) =>
            activeTab === j && (
              <TabsGridUi.Tab key={tab._uid} isActive={activeTab === j}>
                <TabsGridUi.TabGrid layout={tab.layout}>
                  {tab.items.map((item, k) => (
                    <TabsGridUi.TabGridItem
                      key={item._uid}
                      title={item.title}
                      description={item.description}
                      buttons={createItemButtonCollection(
                        item.buttons,
                        item.cover
                      )}
                      visualType={item.visualType}
                      href={
                        item.buttons?.[0]?.buttonLink
                          ? resolveLink(item.buttons[0].buttonLink)
                          : undefined
                      }
                      image={
                        item.image &&
                        isValidImageData(item.image) && (
                          <StoryblokImage
                            image={item.image}
                            layout="fill"
                            sizes="100vw"
                            resize={{ width: 720 }}
                            objectFit={item.cover ? "cover" : "contain"}
                            priority
                            alt={item.title}
                          />
                        )
                      }
                      video={
                        item.video?.filename && (
                          <VideoPlayer
                            key={item.video.id}
                            videoUrl={item.video.filename}
                            thumbnail={
                              item.image &&
                              isValidImageData(item.image) && (
                                <StoryblokImage
                                  image={item.image}
                                  layout="fill"
                                  sizes="100vw"
                                  resize={{ width: 720 }}
                                  objectFit="cover"
                                  priority
                                  alt={item.title}
                                />
                              )
                            }
                            thumbnailMode="thumbnailOnly"
                            isActive={activeTab === j}
                          />
                        )
                      }
                      icon={
                        item.icon && (
                          <Image
                            src={`/svg/icons/${item.icon}.svg`}
                            width={40}
                            height={40}
                            alt={item.title}
                          />
                        )
                      }
                      cover={item.cover}
                      overlayStyle={item.overlayStyle}
                      overlayOpacity={item.overlayOpacity}
                      index={k}
                    />
                  ))}
                </TabsGridUi.TabGrid>
              </TabsGridUi.Tab>
            )
        )}
      </AnimatePresence>
    </TabsGridUi>
  );
};

export default TabsGrid;
